/* eslint-disable max-lines */
/* eslint-disable @typescript-eslint/no-unsafe-member-access */
import { type DictationEditorId } from '@/atoms/dictationAtom';
import { store } from '@/store';
import DictationAudioChunked from '@/utils/dictateRecorderChunked';
import {
  type DictationRecorder,
  getDictationTransport,
} from '@/utils/dictateRecorderTransport';
import DictationAudioV2 from '@/utils/dictateRecorderV2';
import { armDictateReengagementCard } from '@heidi/dictate/data';
// import { NullAnalytics } from '@segment/analytics-next/dist/types/core/analytics';
import { isEditorLive, whenEditorLive } from '@/components/tiptap/shared/safe-editor';
import { simpleMarkdownToHtml } from '@/helpers/markdownToHtml';
import { customFetchEventSource } from '@/utils/fetchEventSource';
import { getAuthTokenForHttpClientSync } from '@/utils/http/getAuthTokenForHttpClientSync';
import { logger } from '@/utils/logger/logger';
import { toast } from '@heidi/design-system/toast';
import { sanitizeError } from '@heidi/runtime/logger';
import { DictationCleanupModes } from '@heidi/schema/ml-scribe';
import { Plugin, type Transaction } from '@tiptap/pm/state';
import { type Editor, Extension } from '@tiptap/react';
import { getDictationAtomStatus, updateDictationAtomStatus } from './utils';

interface DictateOptions {
  shouldShowCursor?: boolean;
  shouldInformTab?: boolean;
  sessionId?: string;
  dictationMode?: DictationCleanupModes;
  editorId?: DictationEditorId;
  onSuccess?: () => void;
}
declare module '@tiptap/react' {
  interface Commands<ReturnType> {
    dictation: {
      onClickFromSlashCommand: (options?: DictateOptions) => ReturnType;
      startSpeechRecognition: (options?: DictateOptions) => ReturnType;
      stopSpeechRecognition: () => ReturnType;
      setPartialTranscript: (text: string) => ReturnType;
      setConfirmedTranscript: (text: string) => ReturnType;
      setFullTranscript: (text: string) => ReturnType;
      cleanupDictationState: () => ReturnType;
      finalizeDictation: () => ReturnType;
      processAndStreamCleanedTranscript: (
        transcriptToClean: string,
        sessionId?: string
      ) => ReturnType;
    };
  }
  interface Storage {
    dictation: DictationStorage;
    dictationCursor: {
      shouldShowDuringRecording: boolean;
      position: number | null;
      active: boolean;
    };
  }
}

type DictationStorage = {
  recognition: DictationRecorder | null;
  startingPos: number | null;
  endingPos: number | null;
  isLocked: boolean;
  isDictating: boolean;
  shouldInformTab: boolean;
  initialStartingPos: number | null;
  finalEndingPos: number | null;
  fullTranscript: string;
  sessionId: string | null;
  dictationMode: DictationCleanupModes;
  permanentLock: boolean;
  editorId: DictationEditorId | null;
  onSuccess: (() => void) | null;
  activeStreamAbort: AbortController | null;
  streamInsertPos: number | null;
};

// Tags the cleanup stream's own transactions so they are not re-mapped.
const CLEANUP_STREAM_META = 'dictationCleanupStream';

type DictationOptions = Record<string, never>;

// Helper function to get recognition instance with lazy initialization
function getRecognitionInstance(storage: DictationStorage): DictationRecorder {
  storage.recognition =
    getDictationTransport() === 'chunked'
      ? DictationAudioChunked.getInstance()
      : DictationAudioV2.getInstance();
  return storage.recognition;
}

// Maps the stream's stored positions through a single transaction, or aborts
// the stream if the edit removed the text around its anchor. Returns false
// once aborted, so the caller stops mapping later transactions in the same
// dispatch against a dead stream.
function mapStreamPositions(
  storage: DictationStorage,
  transaction: Transaction,
  streamAbort: AbortController
): boolean {
  const { mapping } = transaction;
  const anchor = storage.streamInsertPos;
  if (anchor !== null && mapping.mapResult(anchor, -1).deletedAcross) {
    // A content-sync echo re-applies the same note; the old positions still hold.
    if (transaction.before.eq(transaction.doc)) {
      return true;
    }
    // The text around the insert point is gone (note replaced or deleted).
    streamAbort.abort();
    return false;
  }
  if (storage.initialStartingPos !== null) {
    storage.initialStartingPos = mapping.map(storage.initialStartingPos, 1);
  }
  if (storage.finalEndingPos !== null) {
    storage.finalEndingPos = mapping.map(storage.finalEndingPos, -1);
  }
  if (anchor !== null) {
    storage.streamInsertPos = mapping.map(anchor, -1);
  }
  return true;
}

export const Dictation = Extension.create<DictationOptions, DictationStorage>({
  name: 'dictation',

  addStorage() {
    return {
      recognition: null as DictationRecorder | null,
      startingPos: null,
      endingPos: null,
      isDictating: false,
      isLocked: false,
      shouldInformTab: false,
      initialStartingPos: null,
      finalEndingPos: null,
      fullTranscript: '',
      sessionId: null,
      dictationMode: DictationCleanupModes.WORD_FOR_WORD,
      permanentLock: false,
      editorId: null as DictationEditorId | null,
      onSuccess: null,
      activeStreamAbort: null,
      streamInsertPos: null,
    };
  },

  // The note stays editable while the cleanup stream is in flight, so its
  // stored positions must follow edits it did not make.
  onTransaction({ transaction, appendedTransactions }) {
    const streamAbort = this.storage.activeStreamAbort;
    if (!streamAbort || this.storage.isDictating) {
      return;
    }
    // A plugin (e.g. AutoJoiner) can append its own untagged transaction on
    // top of the stream's own tagged one, and it still shifts positions —
    // so every transaction in the dispatch is mapped, skipping only the
    // ones the stream itself produced.
    for (const tr of [transaction, ...appendedTransactions]) {
      if (!tr.docChanged || tr.getMeta(CLEANUP_STREAM_META)) {
        continue;
      }
      if (!mapStreamPositions(this.storage, tr, streamAbort)) {
        return;
      }
    }
  },

  onDestroy() {
    // A destroyed editor can never receive its transcripts: left running, the
    // recorder keeps the mic hot while the guards silently drop every result.
    // The recorder is a singleton, so only stop a run this editor owns.
    if (this.storage.recognition?.isBoundTo(this.editor)) {
      try {
        this.storage.recognition.stop();
      } catch (error) {
        // TipTap emits `destroy` before it unmounts the view and does not catch
        // hook errors, so a throw here would abandon the editor half-destroyed
        // and skip both releases below.
        logger.error('Failed to stop the dictation recorder on editor destroy', error);
      }
      if (this.storage.isDictating) {
        // Release the atom only for a run this editor owns — after a takeover
        // the atom reflects the new owner's live run.
        this.storage.isDictating = false;
        updateDictationAtomStatus(
          { isDictating: false },
          this.storage.editorId ?? undefined
        );
      }
    }
    // The transcript-cleanup stream is per-editor; stop it with the editor.
    // Releasing isProcessing happens here because the aborted stream's catch
    // no longer owns the slot and will skip its own release. The write also
    // touches the legacy global atom unscoped — same as the old catch did.
    if (this.storage.activeStreamAbort) {
      this.storage.activeStreamAbort.abort();
      this.storage.activeStreamAbort = null;
      updateDictationAtomStatus(
        { isProcessing: false },
        this.storage.editorId ?? undefined
      );
    }
  },

  addCommands() {
    return {
      onClickFromSlashCommand:
        (options?: DictateOptions) =>
        ({ chain }) => {
          if (this.storage.isDictating) {
            chain().stopSpeechRecognition().run();
          } else {
            chain().startSpeechRecognition(options).run();
          }
          return true;
        },
      startSpeechRecognition:
        (options?: DictateOptions) =>
        ({ chain, state }) => {
          // A new dictation reuses initialStartingPos/finalEndingPos for its
          // own run; left alive, the previous cleanup stream's onmessage
          // would keep deleteRange-ing against fields this run just
          // overwrote.
          this.storage.activeStreamAbort?.abort();
          const shouldShowCursor = options?.shouldShowCursor ?? true;
          this.editor.storage.dictationCursor.shouldShowDuringRecording =
            shouldShowCursor;
          this.storage.shouldInformTab = options?.shouldInformTab ?? false;
          this.storage.isDictating = true;
          this.storage.editorId = options?.editorId ?? null;
          this.storage.onSuccess = options?.onSuccess ?? null;
          const editorId = this.storage.editorId ?? undefined;
          updateDictationAtomStatus(
            {
              isDictating: true,
              shouldInformTab: this.storage.shouldInformTab,
            },
            editorId
          );
          this.storage.sessionId = options?.sessionId || null;
          this.storage.dictationMode =
            options?.dictationMode ?? DictationCleanupModes.WORD_FOR_WORD;
          this.storage.permanentLock =
            this.storage.dictationMode === DictationCleanupModes.SMART;
          const token = getAuthTokenForHttpClientSync();
          if (!token) {
            this.storage.permanentLock = false;
            this.storage.isDictating = false;
            this.storage.onSuccess = null;
            return false;
          }
          if (store.getState().timerModel.isRunning) {
            toast({
              tone: 'destructive',
              title:
                'You cannot dictate during recording. Please end your recording or dictate after',
            });
            this.storage.isDictating = false;
            this.storage.permanentLock = false;
            this.storage.onSuccess = null;
            updateDictationAtomStatus({ isDictating: false }, editorId);
            return false;
          }
          if (state.selection.from === state.selection.to && state.selection.from === 1) {
            chain().focus('end').run();
          }
          void getRecognitionInstance(this.storage).onStart({
            token,
            editor: this.editor,
            deviceId: store.getState().audio.selectedAudioSource?.deviceId,
            onSuccess: () => {
              // Fires from the recorder singleton, possibly after this
              // editor is gone.
              whenEditorLive(this.editor, 'dictation.onStart', (editor) => {
                this.storage.isDictating = true;
                updateDictationAtomStatus({ isDictating: true }, editorId);
                const currentPos = editor.state.selection.from;
                this.storage.startingPos = currentPos;
                this.storage.endingPos = currentPos;
                this.storage.initialStartingPos = currentPos;
                chain().showDictationCursor(currentPos).focus().run();
              });
            },
            onError: (errorMessage?: string) => {
              toast({
                tone: 'destructive',
                title: errorMessage || 'Sorry there was an error',
                description: 'Please try again later or contact support',
              });
              this.storage.isDictating = false;
              this.storage.onSuccess = null;
              this.storage.permanentLock = false;
              updateDictationAtomStatus({ isDictating: false }, editorId);
            },
          });

          return true;
        },
      stopSpeechRecognition:
        () =>
        ({ chain }) => {
          const editorId = this.storage.editorId ?? undefined;
          try {
            this.storage.isDictating = false;
            updateDictationAtomStatus(
              {
                isDictating: false,
                isProcessing: this.storage.dictationMode === DictationCleanupModes.SMART,
              },
              editorId
            );

            getRecognitionInstance(this.storage).stop();
            // Hide cursor immediately
            if (
              this.storage.startingPos !== null &&
              this.storage.endingPos !== null &&
              this.storage.endingPos > this.storage.startingPos
            ) {
              const { state } = this.editor;
              const text = state.doc?.textBetween(
                this.storage.startingPos,
                this.storage.endingPos,
                ''
              );
              const shouldAddSpace = text.trim().length > 0;
              chain()
                .focus()
                .deleteRange({
                  from: this.storage.startingPos,
                  to: this.storage.endingPos,
                })
                .insertContentAt(
                  this.storage.startingPos,
                  text + (shouldAddSpace ? ' ' : '')
                )
                .updateCursorPosition(null)
                .hideDictationCursor()
                .run();
            }
            // For smart mode, we'll process the transcript after WebSocket cleanup
            if (this.storage.dictationMode === DictationCleanupModes.SMART) {
              // this is for shimmering the text while the transcript is being streamed back
              if (
                this.storage.initialStartingPos !== null &&
                this.storage.finalEndingPos !== null &&
                this.storage.finalEndingPos > this.storage.initialStartingPos &&
                this.storage.fullTranscript !== ''
              ) {
                const textLength =
                  this.storage.finalEndingPos - this.storage.initialStartingPos;
                const speed = 0.5 + textLength / 44;
                chain()
                  .focus()
                  .setTextSelection({
                    from: this.storage.initialStartingPos,
                    to: this.storage.finalEndingPos,
                  })
                  .setShimmer({ speed: `${speed}s` })
                  .setTextSelection(this.storage.finalEndingPos)
                  .run();
              }
            }
            return true;
          } catch (e) {
            logger.debug('stopSpeechRecognition error', e);
            this.storage.permanentLock = false;
            this.storage.onSuccess = null;
            this.storage.isLocked = false;
            updateDictationAtomStatus({ isProcessing: false }, editorId);
            this.editor.commands.updateCursorPosition(null);
            this.editor.commands.hideDictationCursor();
          }
          return false;
        },
      setPartialTranscript:
        (text: string) =>
        ({ chain }) => {
          // Driven by the recorder's socket, which outlives the editor.
          if (!isEditorLive(this.editor, 'dictation.stopSpeechRecognition')) {
            return false;
          }
          const finalText = buildContentFromTranscript(text);
          const replacedText = finalText.replaceAll('<br>', ' ').replaceAll('<p>', ' ');
          const textLength = replacedText.length;
          if (this.storage.startingPos === null || this.storage.endingPos === null) {
            return false;
          }
          // const textAfter = state.doc.textBetween(
          //   this.storage.endingPos,
          //   this.storage.endingPos,
          //   ''
          // );
          try {
            this.storage.isLocked = true;
            if (this.storage.endingPos !== null && this.storage.startingPos !== null) {
              const newPos = this.storage.startingPos + textLength;
              this.editor.commands.showDictationCursor(newPos);
              chain()
                .focus()
                .deleteRange({
                  from: this.storage.startingPos,
                  to: this.storage.endingPos,
                })
                .insertContentAt(this.storage.startingPos, finalText)
                .setTextSelection({
                  from: this.storage.startingPos,
                  to: this.storage.startingPos + textLength,
                })
                .setColor('#755760')
                .updateAttributes('textStyle', { isUnconfirmedText: true })
                .setTextSelection(this.storage.startingPos + textLength)
                .joinDown()
                // .focus()
                .run();

              // this.storage.lastStartingPos = this.storage.startingPos;
              this.storage.endingPos = newPos;
              this.storage.finalEndingPos = newPos;
              return true;
            }
            this.editor.commands.focus();
          } catch (e) {
            logger.error(e);
          }

          return false;
        },
      setConfirmedTranscript:
        (text: string) =>
        ({ chain }) => {
          if (!isEditorLive(this.editor, 'dictation.insertText')) {
            return false;
          }
          try {
            const finalText = buildContentFromTranscript(text);
            const finalTextWithoutParagraphs = finalText
              .replaceAll('<p>', '')
              .replaceAll('<br><br>', '');
            // const textLength = replacedText.length;
            this.storage.isLocked = false;
            // const { state } = this.editor;
            // if (this.storage.startingPos === null || this.storage.endingPos === null) {
            //   return false;
            // }
            // const textAfter = state.doc.textBetween(
            //   this.storage.endingPos,
            //   this.storage.endingPos,
            //   ''
            // );

            if (this.storage.endingPos !== null && this.storage.startingPos !== null) {
              const content = simpleMarkdownToHtml({
                markdown:
                  finalTextWithoutParagraphs +
                  (finalText.endsWith(' ') ||
                  finalText.endsWith('<p>') ||
                  finalText.endsWith('<br><br>')
                    ? ''
                    : ' '),
                options: {
                  paragraphs: false,
                },
              });

              const newlineOffset = (content.match(/<p>/g) || []).length + 1 || 1;
              chain()
                .focus()
                .deleteRange({
                  from: this.storage.startingPos,
                  to: this.storage.endingPos,
                })

                .insertContentAt(this.storage.startingPos, content)
                .run();

              if (finalText.endsWith('<p>') || finalText.endsWith('<br><br>')) {
                setTimeout(() => {
                  whenEditorLive(this.editor, 'dictation.streamInsert', (editor) => {
                    this.storage.startingPos =
                      (this.storage.endingPos || 0) + newlineOffset;
                    this.storage.endingPos =
                      (this.storage.endingPos || 0) + newlineOffset;
                    editor.commands.setHardBreak();
                    editor.commands.enter();
                    editor.commands.enter();
                    editor.commands.joinDown();
                    editor.commands.setPartialTranscript('');
                    editor.commands.setConfirmedTranscript('');
                    editor.commands.focus();
                  });
                }, 1);
                return true;
              }
              this.storage.startingPos = this.storage.endingPos + newlineOffset;
              this.storage.endingPos = this.storage.endingPos + newlineOffset;
              this.storage.finalEndingPos = this.storage.endingPos;
              this.editor.commands.focus();
              return true;
            }
            this.editor.commands.focus();
          } catch (e) {
            logger.error(e);
          }

          return false;
        },
      setFullTranscript: (text: string) => () => {
        this.storage.fullTranscript = this.storage.fullTranscript + text;
        return true;
      },
      finalizeDictation: () => () => {
        const editorId = this.storage.editorId ?? undefined;
        const hasTranscript = this.storage.fullTranscript.trim().length > 0;

        // Fired by the recorder after stop; the editor may already be gone.
        // The dictation atom is global, so it must still be released.
        if (!isEditorLive(this.editor, 'dictation.updateCursor')) {
          updateDictationAtomStatus({ isProcessing: false }, editorId);
          return true;
        }

        if (this.storage.dictationMode === DictationCleanupModes.SMART && hasTranscript) {
          const effectiveSessionId =
            this.storage.sessionId || getDictationAtomStatus(editorId).sessionId;
          this.editor.commands.processAndStreamCleanedTranscript(
            this.storage.fullTranscript,
            effectiveSessionId
          );
          return true;
        }

        const onSuccess = hasTranscript ? this.storage.onSuccess : null;
        updateDictationAtomStatus({ isProcessing: false }, editorId);
        this.editor.commands.cleanupDictationState();
        if (hasTranscript) {
          armDictateReengagementCard('inline');
        }
        onSuccess?.();
        return true;
      },
      cleanupDictationState: () => () => {
        this.storage.startingPos = null;
        this.storage.endingPos = null;
        this.storage.isLocked = false;
        this.storage.permanentLock = false;
        this.storage.fullTranscript = '';
        this.storage.onSuccess = null;
        this.storage.streamInsertPos = null;
        return true;
      },
      processAndStreamCleanedTranscript:
        (transcriptToClean: string, sessionId?: string) => () => {
          const editorId = this.storage.editorId ?? undefined;
          // Don't process empty or whitespace-only transcripts
          if (!transcriptToClean || !transcriptToClean.trim()) {
            updateDictationAtomStatus({ isProcessing: false }, editorId);
            this.storage.permanentLock = false;
            this.editor.commands.cleanupDictationState();
            return true;
          }

          // Mark processing state as active while we stream the cleaned transcript
          updateDictationAtomStatus({ isProcessing: true }, editorId);

          const precedingText = this.editor.state.doc.textBetween(
            0,
            this.storage.initialStartingPos || 0,
            '\n'
          );

          const cleanupEndpointPath = sessionId
            ? `sessions/${sessionId}/dictate/live-dictation`
            : `templates/template-creation-record/dictate/live-dictation`;

          // A replacement stream supersedes the previous one — two live
          // streams would interleave inserts into the same note.
          this.storage.activeStreamAbort?.abort();
          const streamAbort = new AbortController();
          this.storage.activeStreamAbort = streamAbort;
          this.storage.streamInsertPos = this.storage.initialStartingPos;

          customFetchEventSource({
            path: cleanupEndpointPath,
            endPointVersion: 'v2',
            requestBody: {
              dictation_content: transcriptToClean,
              preceding_text:
                this.storage.dictationMode === DictationCleanupModes.SMART
                  ? precedingText
                  : undefined,
            },
            onopen: () => {},
            onmessage: (event) => {
              if (!isEditorLive(this.editor, 'dictation.handleError')) {
                return;
              }
              // Same ownership gate as onclose/catch — a superseded stream's
              // in-flight chunk must not interleave with its replacement.
              if (
                this.storage.activeStreamAbort !== streamAbort ||
                streamAbort.signal.aborted
              ) {
                return;
              }
              try {
                // Delete the shimmered text when the stream is ready
                if (
                  this.storage.initialStartingPos !== null &&
                  this.storage.finalEndingPos !== null &&
                  this.storage.finalEndingPos > this.storage.initialStartingPos
                ) {
                  const deleteFrom = this.storage.initialStartingPos;
                  this.editor
                    .chain()
                    .focus()
                    .deleteRange({
                      from: this.storage.initialStartingPos,
                      to: this.storage.finalEndingPos,
                    })
                    .setMeta(CLEANUP_STREAM_META, true)
                    .run();
                  // Read the position after dispatch: onTransaction may have
                  // already mapped it through an appended transaction (e.g.
                  // AutoJoiner) from this same run(), and that would
                  // otherwise be clobbered by reusing the pre-run value.
                  const insertAfterDelete = this.storage.streamInsertPos ?? deleteFrom;
                  this.storage.finalEndingPos = insertAfterDelete;
                  this.storage.initialStartingPos = null;
                  this.storage.streamInsertPos = insertAfterDelete;
                }
                const chunk = String(event.data);
                const processedChunk = chunk.replace(/\n/g, '<br>');
                const currentInsertPos = this.storage.streamInsertPos;

                if (currentInsertPos !== null && processedChunk.trim()) {
                  // Clamp position to document length to prevent RangeError
                  const docSize = this.editor.state.doc.content.size;
                  const safeInsertPos = Math.min(currentInsertPos, docSize);
                  if (currentInsertPos > docSize) {
                    logger.debug('Dictation insert position clamped', {
                      currentInsertPos,
                      docSize,
                    });
                  }

                  this.editor
                    .chain()
                    .insertContentAt(safeInsertPos, processedChunk)
                    .setMeta(CLEANUP_STREAM_META, true)
                    .run();

                  const nextInsertPos = this.editor.state.selection.to;
                  this.storage.streamInsertPos = nextInsertPos;
                  this.storage.finalEndingPos = nextInsertPos;

                  this.editor.chain().setTextSelection(nextInsertPos).focus().run();
                }
              } catch (error) {
                // Runs inside a setTimeout, so an escaping throw is unhandled
                // and repeats for every later chunk. End the stream instead.
                logger.error('Dropped a dictation cleanup chunk', {
                  errorName: error instanceof Error ? error.name : 'unknown',
                  docSize: this.editor.state.doc.content.size,
                });
                streamAbort.abort();
              }
            },
            onclose: () => {
              // Only the stream that owns the slot may release shared state —
              // a superseded stream's completion would clobber its
              // replacement's in-flight run.
              if (this.storage.activeStreamAbort !== streamAbort) {
                return;
              }
              const onSuccess = this.storage.onSuccess;
              this.storage.activeStreamAbort = null;
              updateDictationAtomStatus({ isProcessing: false }, editorId);
              this.storage.permanentLock = false;
              whenEditorLive(this.editor, 'dictation.finalize', (editor) => {
                clearCleanupShimmer(editor);
                editor.commands.cleanupDictationState();
              });
              armDictateReengagementCard('inline');
              onSuccess?.();
            },
            abortController: streamAbort,
          }).catch((error) => {
            logger.debug('processAndStreamCleanedTranscript error', sanitizeError(error));
            // Same ownership gate: an aborted (superseded or destroyed)
            // stream's rejection must not release its replacement's state.
            if (this.storage.activeStreamAbort !== streamAbort) {
              return;
            }
            this.storage.activeStreamAbort = null;
            this.storage.permanentLock = false;
            updateDictationAtomStatus({ isProcessing: false }, editorId);
            whenEditorLive(this.editor, 'dictation.stopSuccess', (editor) => {
              clearCleanupShimmer(editor);
              editor.commands.cleanupDictationState();
            });
          });
          this.storage.permanentLock = false;
          return true;
        },
    };
  },

  addProseMirrorPlugins() {
    // const storage = this.storage;
    return [
      new Plugin({
        props: {
          handleDOMEvents: {
            mousedown: (_, e) => {
              if (this.storage.isLocked || this.storage.permanentLock) {
                e.preventDefault();
                return true;
              }
              return false;
            },
            mouseup: (_, e) => {
              if (this.storage.isLocked || this.storage.permanentLock) {
                e.preventDefault();
                return false;
              }
              setTimeout(() => {
                whenEditorLive(this.editor, 'dictation.cursorShow', (editor) => {
                  this.storage.startingPos = editor.state.selection.to;
                  this.storage.endingPos = editor.state.selection.to;
                  editor.commands.updateCursorPosition(editor.state.selection.to);
                });
              }, 1);
              return true;
            },
            keyup: (_, e) => {
              if (this.storage.isLocked || this.storage.permanentLock) {
                e.preventDefault();
                return false;
              }
              setTimeout(() => {
                whenEditorLive(this.editor, 'dictation.cursorHide', (editor) => {
                  editor.commands.updateCursorPosition(editor.state.selection.to);
                  this.storage.startingPos = editor.state.selection.to;
                  this.storage.endingPos = editor.state.selection.to;
                });
              }, 1);
              return true;
            },
            keydown: (_, e) => {
              if (this.storage.isLocked || this.storage.permanentLock) {
                e.preventDefault();
                return false;
              }
              return false;
            },
          },
        },
      }),
    ];
  },
});

// Only dictation sets shimmer, and none of it should outlive the cleanup stream.
function clearCleanupShimmer(editor: Editor) {
  const shimmer = editor.schema.marks.shimmer;
  if (!shimmer) {
    return;
  }
  const { tr, doc } = editor.state;
  tr.removeMark(0, doc.content.size, shimmer);
  if (tr.docChanged) {
    editor.view.dispatch(tr);
  }
}

function buildContentFromTranscript(text: string) {
  return text.replace('<\\n\\n>', '\n\n').replace(/(<\\n>)+/g, '\n');
}
